import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosClient from '../../api/axiosClient';
import { 
  Shield, Lock, Globe, Plus, Trash2, Unlock, 
  Activity, Smartphone, Monitor, AlertOctagon
} from 'lucide-react';
import { usePermission } from '../../hooks/usePermission';

const AccessControlPage = () => {
  const { can } = usePermission();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('whitelist');
  
  // Form State for Whitelist
  const [newIp, setNewIp] = useState('');
  const [newLabel, setNewLabel] = useState('');

  // --- QUERIES ---
  const { data: whitelist = [] } = useQuery({
    queryKey: ['officeIps'],
    queryFn: () => axiosClient.get('/admin/security/office-ips')
  });

  const { data: blacklist = [] } = useQuery({
    queryKey: ['blockedIps'],
    queryFn: () => axiosClient.get('/admin/security/blocked-ips')
  });

  const { data: auditLogs = [] } = useQuery({
    queryKey: ['loginAudit'],
    queryFn: () => axiosClient.get('/admin/security/login-audit'),
    refetchInterval: 5000 // Live updates for security logs
  });

  // --- MUTATIONS ---
  const addIpMutation = useMutation({
    mutationFn: () => axiosClient.post('/admin/security/office-ips', { ip: newIp, label: newLabel }),
    onSuccess: () => {
      queryClient.invalidateQueries(['officeIps']);
      setNewIp('');
      setNewLabel('');
    },
    onError: (err) => alert("Failed to add IP. Ensure valid CIDR format (e.g. 192.168.1.1).")
  });

  const removeIpMutation = useMutation({
    mutationFn: (id) => axiosClient.delete(`/admin/security/office-ips/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['officeIps'])
  });

  const unblockIpMutation = useMutation({
    mutationFn: (id) => axiosClient.delete(`/admin/security/blocked-ips/${id}`),
    onSuccess: () => queryClient.invalidateQueries(['blockedIps'])
  });

  return (
    <div className="space-y-6 animate-fade-in pb-10">
      
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center">
          <Shield className="w-8 h-8 text-blue-600 mr-3" />
          Access Control & Security
        </h1>
        <p className="text-slate-500 mt-1">Manage IP restrictions and monitor login attempts.</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex space-x-8">
          <button 
            onClick={() => setActiveTab('whitelist')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center ${activeTab === 'whitelist' ? 'border-blue-500 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            <Globe className="w-4 h-4 mr-2" /> Office Network (Whitelist)
          </button>
          <button 
            onClick={() => setActiveTab('blacklist')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center ${activeTab === 'blacklist' ? 'border-red-500 text-red-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            <Lock className="w-4 h-4 mr-2" /> Blocked IPs (Blacklist)
          </button>
          <button 
            onClick={() => setActiveTab('audit')}
            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors flex items-center ${activeTab === 'audit' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          >
            <Activity className="w-4 h-4 mr-2" /> Login Audit Trail
          </button>
        </nav>
      </div>

      {/* --- TAB CONTENT --- */}

      {/* 1. WHITELIST TAB */}
      {activeTab === 'whitelist' && can('settings:manage') && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-in slide-in-from-left-4">
           {/* Add Form */}
           <div className="bg-white p-6 rounded-xl border border-blue-100 shadow-sm h-fit">
              <h3 className="font-bold text-slate-800 mb-4 flex items-center"><Plus className="w-5 h-5 mr-2 text-blue-500"/> Add Authorized IP</h3>
              <p className="text-xs text-slate-500 mb-4">Staff members can ONLY login from these IPs. Super Admins are exempt.</p>
              
              <div className="space-y-4">
                 <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">IP Address / CIDR</label>
                    <input className="w-full p-2 border rounded mt-1 font-mono" placeholder="203.0.113.5" value={newIp} onChange={e => setNewIp(e.target.value)} />
                 </div>
                 <div>
                    <label className="text-xs font-bold text-slate-500 uppercase">Label / Location</label>
                    <input className="w-full p-2 border rounded mt-1" placeholder="HQ Main Office" value={newLabel} onChange={e => setNewLabel(e.target.value)} />
                 </div>
                 <button 
                   onClick={() => addIpMutation.mutate()}
                   disabled={!newIp || !newLabel}
                   className="w-full bg-blue-600 text-white py-2 rounded font-bold hover:bg-blue-700 disabled:opacity-50"
                 >
                   Authorize Network
                 </button>
              </div>
           </div>

           {/* List */}
           <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <table className="w-full text-left">
                 <thead className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase">
                    <tr><th className="px-6 py-4">Network</th><th className="px-6 py-4">Location</th><th className="px-6 py-4 text-right">Action</th></tr>
                 </thead>
                 <tbody className="divide-y divide-slate-50">
                    {whitelist.length === 0 ? (
                        <tr><td colSpan="3" className="p-8 text-center text-slate-400">No restrictions. Staff can login from anywhere (Not Recommended).</td></tr>
                    ) : whitelist.map(ip => (
                        <tr key={ip.id} className="hover:bg-slate-50">
                           <td className="px-6 py-4 font-mono text-slate-700">{ip.ip_cidr}</td>
                           <td className="px-6 py-4 text-slate-600">{ip.label}</td>
                           <td className="px-6 py-4 text-right">
                              <button onClick={() => removeIpMutation.mutate(ip.id)} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4"/></button>
                           </td>
                        </tr>
                    ))}
                 </tbody>
              </table>
           </div>
        </div>
      )}

      {/* 2. BLACKLIST TAB */}
      {activeTab === 'blacklist' && (
         <div className="bg-white rounded-xl border border-red-100 shadow-sm overflow-hidden animate-in slide-in-from-right-4">
             <div className="p-4 bg-red-50/50 border-b border-red-100 flex items-center text-red-800 font-medium">
                <AlertOctagon className="w-5 h-5 mr-2" /> Automatically Blocked IPs (Brute Force Defense)
             </div>
             <table className="w-full text-left">
                 <thead className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase">
                    <tr><th className="px-6 py-4">IP Address</th><th className="px-6 py-4">Reason</th><th className="px-6 py-4">Blocked Until</th><th className="px-6 py-4 text-right">Action</th></tr>
                 </thead>
                 <tbody className="divide-y divide-slate-50">
                    {blacklist.length === 0 ? (
                        <tr><td colSpan="4" className="p-8 text-center text-slate-400">No active blocks. System is clean.</td></tr>
                    ) : blacklist.map(ip => (
                        <tr key={ip.id} className="hover:bg-red-50/30">
                           <td className="px-6 py-4 font-mono text-red-600 font-bold">{ip.ip_address}</td>
                           <td className="px-6 py-4 text-slate-600">{ip.reason}</td>
                           <td className="px-6 py-4 text-slate-500">{new Date(ip.blocked_until).toLocaleString()}</td>
                           <td className="px-6 py-4 text-right">
                              <button 
                                onClick={() => unblockIpMutation.mutate(ip.id)}
                                className="px-3 py-1 bg-white border border-slate-200 text-slate-600 text-xs font-bold rounded hover:bg-slate-50 hover:text-green-600 flex items-center ml-auto"
                              >
                                <Unlock className="w-3 h-3 mr-1"/> Unblock
                              </button>
                           </td>
                        </tr>
                    ))}
                 </tbody>
             </table>
         </div>
      )}

      {/* 3. AUDIT LOG TAB */}
      {activeTab === 'audit' && (
         <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden animate-in fade-in">
             <table className="w-full text-left">
                 <thead className="bg-slate-50 border-b border-slate-200 text-xs font-bold text-slate-500 uppercase">
                    <tr><th className="px-6 py-4">Time</th><th className="px-6 py-4">User</th><th className="px-6 py-4">IP Address</th><th className="px-6 py-4">Device</th><th className="px-6 py-4 text-right">Result</th></tr>
                 </thead>
                 <tbody className="divide-y divide-slate-50">
                    {auditLogs.map((log) => (
                        <tr key={log.id} className="hover:bg-slate-50">
                           <td className="px-6 py-4 text-slate-500 text-sm">{new Date(log.timestamp).toLocaleString()}</td>
                           <td className="px-6 py-4 font-medium text-slate-700">{log.email}</td>
                           <td className="px-6 py-4 font-mono text-xs text-slate-500">{log.ip_address}</td>
                           <td className="px-6 py-4 text-slate-600 flex items-center">
                              {log.device_type === 'Mobile' ? <Smartphone className="w-4 h-4 mr-2"/> : <Monitor className="w-4 h-4 mr-2"/>}
                              {log.device_type}
                           </td>
                           <td className="px-6 py-4 text-right">
                              {log.status === 'SUCCESS' && <span className="text-xs font-bold text-green-600 bg-green-100 px-2 py-1 rounded">SUCCESS</span>}
                              {log.status === 'FAILED_PASSWORD' && <span className="text-xs font-bold text-orange-600 bg-orange-100 px-2 py-1 rounded">WRONG PASS</span>}
                              {log.status.includes('BLOCKED') && <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-1 rounded">BLOCKED</span>}
                              {log.status === 'GEO_RESTRICTED' && <span className="text-xs font-bold text-slate-600 bg-slate-200 px-2 py-1 rounded">RESTRICTED IP</span>}
                           </td>
                        </tr>
                    ))}
                 </tbody>
             </table>
         </div>
      )}

    </div>
  );
};

export default AccessControlPage;